"use client";

import { useState } from "react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import Icon from "@/components/ui/AppIcon";

interface FormData {
  fullName: string;
  phone: string;
  whatsapp: string;
  location: string;
  careType: string;
  preferredDate: string;
  message: string;
}

const careTypes = [
  "Bedside Nursing Care",
  "Elderly Care",
  "Medication Management",
  "Wound Care & Dressing",
  "Mobility & Physiotherapy Support",
  "Personal Hygiene Assistance",
  "Vital Signs Monitoring",
  "Palliative & End-of-Life Care",
  "Multiple Services",
];

const locations = [
  "Kololo", "Nakasero", "Naguru", "Bugolobi", "Ntinda", "Mbuya Hill",
  "Muyenga", "Munyonyo", "Buziga", "Bukoto", "Nalya", "Makindye",
  "Mutungo", "Kiwatule", "Kamwokya", "Bunga", "Other / Not Listed",
];

export default function ContactSection() {
  const sectionRef = useScrollAnimation<HTMLElement>();

  const [formData, setFormData] = useState<FormData>({
    fullName: '', phone: '', whatsapp: '', location: '',
    careType: '', preferredDate: '', message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        alert('Failed to submit form. Please try WhatsApp or call us directly.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert('Failed to submit form. Please try WhatsApp or call us directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="navy-section py-24 md:py-32"
      aria-labelledby="contact-heading"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left side info */}
          <div className="reveal-up stagger-1">
            <span className="inline-block text-xs font-semibold tracking-widest uppercase font-sans mb-4 px-4 py-2 rounded-full" style={{ color: 'var(--gold)', background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)' }}>
              Contact Beecon
            </span>
            <h2 id="contact-heading" className="font-serif text-4xl md:text-5xl font-light text-white leading-tight mt-4 mb-5">
              Request a{" "}
              <span className="italic gold-text">Home Visit</span>
            </h2>
            <p className="text-lg leading-relaxed font-sans mb-8" style={{ color: 'rgba(255,255,255,0.55)' }}>
              Fill in the form and we&apos;ll respond within 2 hours. Or reach us instantly on WhatsApp — we&apos;re available 24/7 for urgent care requests.
            </p>

            {/* Contact methods */}
            <div className="space-y-4 mb-10">
              <a
                href="https://wa.me/256788217719"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-4 rounded-2xl border transition-all duration-300 group hover:border-emerald-400/40"
                style={{ background: 'rgba(37,211,102,0.06)', borderColor: 'rgba(37,211,102,0.2)' }}
                aria-label="Chat with Beecon on WhatsApp"
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(37,211,102,0.12)' }}>
                  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="#25D366" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </div>
                <div>
                  <div className="text-sm font-semibold text-white font-sans">WhatsApp — Instant Response</div>
                  <div className="text-sm font-sans" style={{ color: '#25D366' }}>+256 788 217 719</div>
                </div>
                <Icon name="ArrowRightIcon" size={16} className="ml-auto opacity-40 group-hover:opacity-80 transition-opacity text-white" />
              </a>

              <div className="flex items-center gap-4 p-4 rounded-2xl border" style={{ background: 'rgba(201,168,76,0.04)', borderColor: 'rgba(201,168,76,0.15)' }}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(201,168,76,0.1)' }}>
                  <Icon name="PhoneIcon" size={22} variant="solid" style={{ color: 'var(--gold)' }} />
                </div>
                <div>
                  <div className="text-sm font-semibold text-white font-sans">Phone Call</div>
                  <div className="text-sm font-sans" style={{ color: 'var(--gold)' }}>+256 788 217 719</div>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 rounded-2xl border" style={{ background: 'rgba(201,168,76,0.04)', borderColor: 'rgba(201,168,76,0.15)' }}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(201,168,76,0.1)' }}>
                  <Icon name="EnvelopeIcon" size={22} variant="solid" style={{ color: 'var(--gold)' }} />
                </div>
                <div>
                  <div className="text-sm font-semibold text-white font-sans">Email</div>
                  <a href="mailto:beeconhealthcare@gmail.com" className="text-sm font-sans hover:underline" style={{ color: 'var(--gold)' }}>beeconhealthcare@gmail.com</a>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 rounded-2xl border" style={{ background: 'rgba(201,168,76,0.04)', borderColor: 'rgba(201,168,76,0.15)' }}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(201,168,76,0.1)' }}>
                  <Icon name="MapPinIcon" size={22} variant="solid" style={{ color: 'var(--gold)' }} />
                </div>
                <div>
                  <div className="text-sm font-semibold text-white font-sans">Coverage Area</div>
                  <div className="text-sm font-sans" style={{ color: 'rgba(255,255,255,0.5)' }}>Kampala & surrounding areas, Uganda</div>
                </div>
              </div>
            </div>

            {/* Urgency note */}
            <div className="rounded-2xl p-5 border" style={{ background: 'rgba(201,168,76,0.06)', borderColor: 'rgba(201,168,76,0.2)' }}>
              <div className="flex items-start gap-3">
                <Icon name="BoltIcon" size={18} variant="solid" style={{ color: 'var(--gold)', flexShrink: 0, marginTop: 2 }} />
                <p className="text-sm leading-relaxed font-sans" style={{ color: 'rgba(255,255,255,0.65)' }}>
                  <strong className="text-white">Urgent care request?</strong> WhatsApp us directly for same-day nurse deployment across all Kampala service areas.
                </p>
              </div>
            </div>
          </div>

          {/* Right side form */}
          <div className="reveal-up stagger-2 glass-card p-7 md:p-10">
            {submitted ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mb-5" style={{ background: 'rgba(201,168,76,0.15)' }}>
                  <Icon name="CheckCircleIcon" size={36} variant="solid" style={{ color: 'var(--gold)' }} />
                </div>
                <h3 className="font-serif text-2xl font-light text-white mb-3">Request Received!</h3>
                <p className="text-sm font-sans leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)' }}>
                  Thank you. A Beecon care coordinator will contact you within 2 hours. For urgent requests, please WhatsApp us directly.
                </p>
                <a
                  href="https://wa.me/256788217719"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-gold mt-6 px-6 py-3 text-sm font-semibold inline-flex items-center justify-center"
                >
                  Chat on WhatsApp
                </a>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate aria-label="Request a home visit from Beecon">
                <h3 className="font-serif text-2xl font-light text-white mb-6">Book a Home Visit</h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="fullName" className="block text-xs font-semibold uppercase tracking-wider font-sans mb-2" style={{ color: 'rgba(201,168,76,0.7)' }}>
                      Full Name *
                    </label>
                    <input
                      id="fullName"
                      name="fullName"
                      type="text"
                      required
                      placeholder="Your full name"
                      className="form-input"
                      value={formData.fullName}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-xs font-semibold uppercase tracking-wider font-sans mb-2" style={{ color: 'rgba(201,168,76,0.7)' }}>
                      Phone Number *
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      placeholder="+256 7XX XXX XXX"
                      className="form-input"
                      value={formData.phone}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="whatsapp" className="block text-xs font-semibold uppercase tracking-wider font-sans mb-2" style={{ color: 'rgba(201,168,76,0.7)' }}>
                      WhatsApp Number
                    </label>
                    <input
                      id="whatsapp"
                      name="whatsapp"
                      type="tel"
                      placeholder="+256 7XX XXX XXX"
                      className="form-input"
                      value={formData.whatsapp}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <label htmlFor="location" className="block text-xs font-semibold uppercase tracking-wider font-sans mb-2" style={{ color: 'rgba(201,168,76,0.7)' }}>
                      Your Neighbourhood *
                    </label>
                    <select
                      id="location"
                      name="location"
                      required
                      className="form-input"
                      value={formData.location}
                      onChange={handleChange}
                    >
                      <option value="">Select area...</option>
                      {locations.map(loc => (
                        <option key={`loc-${loc}`} value={loc}>{loc}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="careType" className="block text-xs font-semibold uppercase tracking-wider font-sans mb-2" style={{ color: 'rgba(201,168,76,0.7)' }}>
                      Type of Care *
                    </label>
                    <select
                      id="careType"
                      name="careType"
                      required
                      className="form-input"
                      value={formData.careType}
                      onChange={handleChange}
                    >
                      <option value="">Select care type...</option>
                      {careTypes.map(ct => (
                        <option key={`care-${ct}`} value={ct}>{ct}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="preferredDate" className="block text-xs font-semibold uppercase tracking-wider font-sans mb-2" style={{ color: 'rgba(201,168,76,0.7)' }}>
                      Preferred Start Date
                    </label>
                    <input
                      id="preferredDate"
                      name="preferredDate"
                      type="date"
                      className="form-input"
                      value={formData.preferredDate}
                      onChange={handleChange}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-xs font-semibold uppercase tracking-wider font-sans mb-2" style={{ color: 'rgba(201,168,76,0.7)' }}>
                    Tell Us More (Optional)
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    placeholder="Describe the patient's condition, care needs, or any special requirements..."
                    className="form-input resize-none"
                    value={formData.message}
                    onChange={handleChange}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-gold w-full py-4 text-base font-bold flex items-center justify-center gap-3"
                  aria-label="Submit home visit request"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-navy/30 border-t-navy rounded-full animate-spin" />
                      Sending Request...
                    </>
                  ) : (
                    <>
                      <Icon name="PaperAirplaneIcon" size={18} variant="solid" />
                      Send Care Request
                    </>
                  )}
                </button>

                <p className="text-xs text-center mt-4 font-sans" style={{ color: 'rgba(255,255,255,0.3)' }}>
                  We respond within 2 hours · Your information is kept strictly confidential
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}